import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { TkticMultiSelect } from "@/components/ui/TkticMultiSelect";
import { ToneChipGroup } from "./ToneChipGroup";
import { AIDraftPanel } from "./AIDraftPanel";
import type { CoachingVisibility, Player } from "@/lib/types";

/**
 * Composer form — the side-panel form for authoring a note or
 * observation in Coach Review.
 *
 * Field set matches the structured-feedback fields the backend's
 * `CreateCoachingNoteRequest` accepts. Mirror — not deeper than —
 * the Pydantic schema:
 *
 *   - title              (required)
 *   - note_type          (closed enum)
 *   - category           (closed enum; defaults to "shape")
 *   - visibility         (CoachingVisibility, defaults to "team")
 *   - body               (long-form free notes; backend max 4000)
 *   - player_summary     (player-facing line)
 *   - what_happened      (observation)
 *   - why_it_matters     (context)
 *   - what_to_do_next    (actionable)
 *   - coach_private_note (coach-only; defaults to "")
 *   - player_ids[]       (tagged players, multi-select)
 *   - tags[]             (free tags, parsed from comma-separated input)
 *   - event_title/date/type (observation mode only)
 *
 * Match-specific + observation-specific fields (timestamp_seconds,
 * event_date, etc.) are owned by the parent shell — the composer is
 * the structured-text part only. The shell composes the final payload
 * by merging the form values with the canvas data
 * (`tactical_board_json` / `drawing`) at submit time.
 *
 * Save action is provided by the parent via `onSubmit` — the
 * composer doesn't know which endpoint it's saving to. In M4 the
 * parent always saves a coaching NOTE (video-context when a match
 * is in scope, observation-context otherwise).
 *
 * Save Clip is NOT part of M4 and is intentionally absent here.
 * Clip authoring needs the M5 HLS video player: a clip is a
 * `[start_seconds, end_seconds]` window and those bounds come from
 * the playing video's `currentTime` (legacy pre-filled
 * `[currentTime − 5s, currentTime + 8s]`). M4 has no player, so a
 * Save Clip control would have nothing to read bounds from. M5
 * adds `useSaveCoachingClip` + a clip mode here once the player
 * lands — the `onSubmit`-injection design already supports it
 * without restructuring this component.
 *
 * Note on the "Structured feedback" disclosure: implemented as a
 * controlled state toggle (NOT a native `<details>` element). The
 * project's auto-memory rule disallows native browser chrome in
 * styled UIs, and `<details>`/`<summary>` has cross-browser marker
 * inconsistencies that the linter actively rejects. Using a plain
 * button + conditional render keeps the chrome 100% styled.
 */

// Note category — a CLOSED enum, not a free string. Mirrors the
// backend's `_VALID_NOTE_CATEGORIES` set in `models.py` (the
// `CreateCoachingNoteRequest.validate_category` validator 422s
// anything outside it). The template registry only ever uses
// values from this set, so applying a template can't desync the
// dropdown. Keep this list in lockstep with `_VALID_NOTE_CATEGORIES`.
export const NOTE_CATEGORIES = [
  "shape",
  "pressing",
  "transition",
  "set_piece",
  "build_up",
  "finishing",
  "defending",
  "goalkeeper",
  "effort",
  "decision",
  "other",
] as const;

export const NOTE_CATEGORY_LABELS: Record<
  (typeof NOTE_CATEGORIES)[number],
  string
> = {
  shape: "Shape",
  pressing: "Pressing",
  transition: "Transition",
  set_piece: "Set piece",
  build_up: "Build-up",
  finishing: "Finishing",
  defending: "Defending",
  goalkeeper: "Goalkeeper",
  effort: "Effort",
  decision: "Decision",
  other: "Other",
};

/** Closed category enum mirrored from the backend. */
export type NoteCategory = (typeof NOTE_CATEGORIES)[number];

/** Narrow a free `string` (from the API note payload or a
 * template) to the closed `NoteCategory` enum. Anything off-list
 * falls back to `"other"` — the same default the backend uses.
 * Used wherever a `string`-typed category crosses into the
 * composer's typed form values. */
export function coerceNoteCategory(value: string | null | undefined): NoteCategory {
  return (NOTE_CATEGORIES as readonly string[]).includes(value ?? "")
    ? (value as NoteCategory)
    : "other";
}

const VISIBILITY_OPTIONS: ReadonlyArray<{
  value: CoachingVisibility;
  label: string;
  description: string;
}> = [
  { value: "private", label: "Coach-only (private)", description: "Only coaches see this." },
  { value: "team", label: "Team", description: "All team members + linked viewers." },
  { value: "player", label: "Player + family", description: "Only tagged players + their family accounts." },
  { value: "unlisted", label: "Unlisted", description: "Direct link only — not in the team feed." },
];

// Observation event type — closed enum mirrored from the backend's
// `_VALID_EVENT_TYPES` set in `models.py`. The empty string is also
// valid backend-side (event_type is optional); the composer's
// dropdown offers a "Practice" default rather than a blank, but the
// schema still permits "" so a seeded note with no event_type
// round-trips.
const EVENT_TYPES = [
  "practice",
  "game",
  "meeting",
  "tactical",
  "other",
] as const;

const EVENT_TYPE_LABELS: Record<(typeof EVENT_TYPES)[number], string> = {
  practice: "Practice",
  game: "Game",
  meeting: "Meeting",
  tactical: "Tactical",
  other: "Other",
};

// `event_date` accepts empty OR `YYYY-MM-DD` — mirrors the backend's
// `_DATE_RE` / `_validate_event_date`. We use a styled text input
// (NOT `<input type="date">`) per the no-native-chrome rule, so the
// schema enforces the format the native picker would have.
const EVENT_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Zod schema for the composer. Defaults intentionally NOT declared
// on optional-text fields here — `.default()` makes the input type
// optional but output required, which trips RHF's resolver generics
// (the form values type then has `string | undefined` for fields
// the resolver expects as `string`). The form's `defaultValues`
// prop on `useForm` covers the same purpose.
//
// The `event_*` fields are always part of the schema (the form is
// one shape), but they only RENDER in observation mode — see the
// `observationMode` prop. In video mode they stay at their
// defaults and the parent ignores them.
export const composerSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  note_type: z.enum([
    "positive",
    "correction",
    "question",
    "team_concept",
    "individual_goal",
  ]),
  category: z.enum(NOTE_CATEGORIES),
  visibility: z.enum(["private", "team", "player", "unlisted"]),
  // Long-form free notes — the backend's `body` column (max 4000).
  // Optional; the structured fields below are the preferred way to
  // capture feedback, but `body` is here so a coach can write
  // free-form AND so editing an existing note that has a `body`
  // doesn't silently drop it.
  body: z.string().max(4000),
  player_summary: z.string().max(2000),
  what_happened: z.string().max(2000),
  why_it_matters: z.string().max(2000),
  what_to_do_next: z.string().max(2000),
  coach_private_note: z.string().max(2000),
  tags_raw: z.string(),
  player_ids: z.array(z.string()),
  event_title: z.string().max(200),
  event_date: z
    .string()
    .refine((v) => v === "" || EVENT_DATE_RE.test(v), {
      message: "Use YYYY-MM-DD or leave blank",
    }),
  event_type: z.enum(["", ...EVENT_TYPES]),
});

export type ComposerValues = z.infer<typeof composerSchema>;

/** Imperative handle the parent uses to read the LIVE form state.
 *
 * Why: the parent shell owns two pieces of behavior that depend on
 * current-instead-of-default values:
 *   1. TemplateSelector's overwrite-protect needs the live values to
 *      decide whether to fire a confirm dialog (a coach who typed
 *      into the title field should be asked before another template
 *      clobbers it).
 *   2. Focus-mode toggle: the inline composer unmounts while the
 *      coach is in canvas-fullscreen mode. The parent snapshots
 *      `getValues()` into `composerOverride` before the unmount so
 *      the remount on close picks up unsaved title / private note
 *      / tags / structured text.
 *
 * `reset()` is also exposed so the parent can clear the form after
 * a successful save without bumping the key (legacy "next moment
 * starts from scratch" behavior). */
export type ComposerHandle = {
  getValues: () => ComposerValues;
  reset: (next?: Partial<ComposerValues>) => void;
};

export type ComposerProps = {
  defaultValues?: Partial<ComposerValues>;
  players: Player[];
  onSubmit: SubmitHandler<ComposerValues>;
  submitLabel?: string;
  hidePlayerPicker?: boolean;
  /** When true, render the observation event fields (event title /
   * date / type). Coach Review passes `source === "tactical_board"`
   * — observations carry event metadata; video notes don't. */
  observationMode?: boolean;
  /** M7.5 F: the active video match's id, forwarded to the AI
   * drafting panel as evidence. Empty string for tactical-board /
   * observation-only composers (the panel still renders with
   * an empty evidence list). */
  matchId?: string;
};

const DEFAULTS: ComposerValues = {
  title: "",
  note_type: "correction",
  category: "shape",
  visibility: "team",
  body: "",
  player_summary: "",
  what_happened: "",
  why_it_matters: "",
  what_to_do_next: "",
  coach_private_note: "",
  tags_raw: "",
  player_ids: [],
  event_title: "",
  event_date: "",
  event_type: "practice",
};

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  {
    defaultValues,
    players,
    onSubmit,
    submitLabel = "Save note",
    hidePlayerPicker = false,
    observationMode = false,
    matchId = "",
  }: ComposerProps,
  ref,
) {
  const form = useForm<ComposerValues>({
    resolver: zodResolver(composerSchema),
    defaultValues: { ...DEFAULTS, ...defaultValues },
  });
  const [structuredOpen, setStructuredOpen] = useState(false);

  useEffect(() => {
    form.reset({ ...DEFAULTS, ...defaultValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(defaultValues)]);

  useImperativeHandle(
    ref,
    () => ({
      getValues: () => form.getValues(),
      reset: (next?: Partial<ComposerValues>) =>
        form.reset({ ...DEFAULTS, ...(next ?? {}) }),
    }),
    [form],
  );

  const visibility = form.watch("visibility");
  const activeVisibility = VISIBILITY_OPTIONS.find((v) => v.value === visibility);

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
      data-testid="coach-review-composer"
    >
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-3">
          <Field label="Title" error={form.formState.errors.title?.message}>
            <input
              type="text"
              {...form.register("title")}
              data-testid="composer-title"
              className="w-full rounded border border-rule bg-canvas px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </Field>

          <Field label="Player-facing summary">
            <textarea
              {...form.register("player_summary")}
              rows={3}
              data-testid="composer-player-summary"
              className="w-full rounded border border-rule bg-canvas px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
            />
          </Field>

          <Field
            label="Full note"
            helper="Visible to players and guardians when this note's visibility allows it."
            error={form.formState.errors.body?.message}
          >
            <textarea
              {...form.register("body")}
              rows={6}
              data-testid="composer-body"
              className="w-full rounded border border-rule bg-canvas px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
            />
          </Field>

          <section className="rounded border border-rule bg-surface">
            <button
              type="button"
              onClick={() => setStructuredOpen((v) => !v)}
              aria-expanded={structuredOpen}
              aria-controls="composer-structured-feedback"
              data-testid="composer-structured-toggle"
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <span>Structured feedback</span>
              <span
                className="inline-block transition-transform"
                style={{ transform: structuredOpen ? "rotate(90deg)" : "rotate(0deg)" }}
                aria-hidden="true"
              >
                ›
              </span>
            </button>
            {structuredOpen ? (
              <div
                id="composer-structured-feedback"
                className="grid gap-3 border-t border-rule p-3 lg:grid-cols-3"
              >
                <Field label="What happened">
                  <textarea
                    {...form.register("what_happened")}
                    rows={4}
                    data-testid="composer-what-happened"
                    className="w-full rounded border border-rule bg-canvas px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
                  />
                </Field>
                <Field label="Why it matters">
                  <textarea
                    {...form.register("why_it_matters")}
                    rows={4}
                    data-testid="composer-why-it-matters"
                    className="w-full rounded border border-rule bg-canvas px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
                  />
                </Field>
                <Field label="What to do next">
                  <textarea
                    {...form.register("what_to_do_next")}
                    rows={4}
                    data-testid="composer-what-to-do-next"
                    className="w-full rounded border border-rule bg-canvas px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
                  />
                </Field>
              </div>
            ) : null}
          </section>

          <Field
            label="Coach-only note"
            helper="Never shown to players or guardians."
          >
            <textarea
              {...form.register("coach_private_note")}
              rows={3}
              data-testid="composer-coach-private-note"
              className="w-full rounded border border-rule bg-canvas px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
            />
          </Field>
        </div>

        <aside className="flex min-w-0 flex-col gap-3 rounded border border-rule bg-surface p-3 shadow-sm">
          {observationMode ? (
            <section
              className="flex flex-col gap-3"
              data-testid="composer-event-fields"
            >
              <Field label="Event title">
                <input
                  type="text"
                  {...form.register("event_title")}
                  data-testid="composer-event-title"
                  placeholder="Tuesday practice"
                  className="w-full rounded border border-rule bg-surface px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field
                  label="Event date"
                  error={form.formState.errors.event_date?.message}
                >
                  <Controller
                    control={form.control}
                    name="event_date"
                    render={({ field }) => (
                      <TkticDateInput
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        data-testid="composer-event-date"
                      />
                    )}
                  />
                </Field>
                <Field label="Event type">
                  <Controller
                    control={form.control}
                    name="event_type"
                    render={({ field }) => (
                      <TkticSelect
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        data-testid="composer-event-type"
                        options={EVENT_TYPES.map((t) => ({
                          value: t,
                          label: EVENT_TYPE_LABELS[t],
                        }))}
                      />
                    )}
                  />
                </Field>
              </div>
            </section>
          ) : null}

          <Field label="Tone">
            <ToneChipGroup
              value={form.watch("note_type")}
              onChange={(next) =>
                form.setValue("note_type", next, { shouldDirty: true })
              }
              data-testid="composer-note-type"
            />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Category">
              <Controller
                control={form.control}
                name="category"
                render={({ field }) => (
                  <TkticSelect
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    data-testid="composer-category"
                    options={NOTE_CATEGORIES.map((c) => ({
                      value: c,
                      label: NOTE_CATEGORY_LABELS[c],
                    }))}
                  />
                )}
              />
            </Field>
            <Field label="Visibility" helper={activeVisibility?.description}>
              <Controller
                control={form.control}
                name="visibility"
                render={({ field }) => (
                  <TkticSelect
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    data-testid="composer-visibility"
                    options={VISIBILITY_OPTIONS.map((v) => ({
                      value: v.value,
                      label: v.label,
                    }))}
                  />
                )}
              />
            </Field>
          </div>

          {!hidePlayerPicker ? (
            <Field label="Tag players">
              <TkticMultiSelect
                value={form.watch("player_ids") ?? []}
                onChange={(next) =>
                  form.setValue("player_ids", next, { shouldDirty: true })
                }
                data-testid="composer-player-ids"
                placeholder="No players tagged"
                options={players.map((p) => ({
                  value: p.id,
                  label: `${p.display_name}${p.jersey_number ? ` · #${p.jersey_number}` : ""}`,
                }))}
              />
            </Field>
          ) : null}

          <Field label="Tags">
            <input
              type="text"
              {...form.register("tags_raw")}
              data-testid="composer-tags"
              placeholder="scanning, decision"
              className="w-full rounded border border-rule bg-canvas px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </Field>

          <AIDraftPanel form={form} matchId={matchId} />

          <button
            type="submit"
            data-testid="composer-submit"
            disabled={form.formState.isSubmitting}
            className="mt-auto rounded bg-accent px-4 py-2 text-sm font-display uppercase tracking-wide text-accent-ink transition-colors hover:bg-accent-hover disabled:bg-field disabled:text-ink-muted"
          >
            {form.formState.isSubmitting ? "Saving…" : submitLabel}
          </button>
        </aside>
      </div>
    </form>
  );
});

/** Field wrapper — label + optional helper text + error display. */
function Field(props: {
  label: string;
  helper?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      {props.children}
      {props.helper ? (
        <span className="text-xs text-ink-muted">{props.helper}</span>
      ) : null}
      {props.error ? (
        <span className="text-xs text-destructive">{props.error}</span>
      ) : null}
    </label>
  );
}

/** Parse the comma-separated tags input into a clean string[]. */
export function parseTagsRaw(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}
